// Kabukicho east edge (x114-127, y10-55): neon bars and a love hotel toward the crossing, a 24h car park, then the
// quieter blocks (flats, coin laundry) that lead out to Okubo. Kind names are prefixed with the region id.
import { hash2 } from '../../common/src/util';
import { K, NEON, type NeonColor, px, speckle, dither, bands, disc, frame, shadow, dk, lt, mix, alpha } from './hd-kit';
import { registerFront, registerRoof, registerRoofProp } from './hd-registry';
import { display, door, lantern, vending, plant, bandSign, bladeSign } from './hd-parts';

// ---------------------------------------------------------------- roof: car park deck with bays and parked cars
registerRoof('kabukiEastDeck', { paint(g, x, y, w, h, seed) {
  px(g, x, y, w, h, K.concrete);
  speckle(g, x, y, w, h, K.concreteLo, 0.05, 41, 2);
  const cols: string[] = [K.pink, K.cyan, K.amber, K.slate, K.white];
  // two rows of bays along the long sides, a drive lane between them
  for (const by of [y + 14, y + h - 56]) {
    for (let i = 14; i + 30 <= w - 8; i += 30) {
      px(g, x + i, by, 1, 42, K.paint); px(g, x + i + 30, by, 1, 42, K.paint);
      const v = hash2(x + i, by + seed);
      if (v < 0.7) {
        const c = dk(cols[Math.floor(hash2(i, seed + by) * cols.length)], 0.45);
        shadow(g, x + i + 7, by + 8, 20, 32, 0.4);
        px(g, x + i + 5, by + 5, 20, 34, K.ink); px(g, x + i + 6, by + 6, 18, 32, c);
        px(g, x + i + 6, by + 6, 18, 2, lt(c, 0.35)); px(g, x + i + 8, by + 12, 14, 8, K.glassLo); px(g, x + i + 8, by + 12, 14, 1, K.glassHi); px(g, x + i + 8, by + 30, 14, 4, K.glassLo);
      }
    }
  }
  for (let i = 8; i < w - 16; i += 24) px(g, x + i, y + h / 2 - 1, 12, 2, K.lane); // lane dashes
} });

// ---------------------------------------------------------------- roof prop: neon board on legs (fixed "BAR")
registerRoofProp('kabukiEastBoard', ({ g, x, y, L }) => {
  shadow(g, x + 6, y + 30, 62, 10, 0.4);
  for (const lx of [8, 56]) px(g, x + lx, y + 26, 3, 10, K.metalLo);
  bandSign(g, x + 34, y, 'BAR', 'pink', L, 2);
});

// ---------------------------------------------------------------- fronts
/** a row of narrow snack bars, each bay with its own neon frame, lantern and little sign */
registerFront('kabukiEastSnack', { wall: dk(K.pink, 0.72) }, ({ g, x, y, w, gf, gy, L }) => {
  const names = ['バー', 'ヤキトリ', 'ゲーム'], cols: NeonColor[] = ['pink', 'amber', 'cyan'], n = 3, bw = Math.floor(w / n);
  for (let i = 0; i < n; i++) {
    const bx = x + i * bw, col = cols[i];
    bandSign(g, bx + bw / 2, y + 14, names[i], col, L, 1);
    px(g, bx + 8, gy - 6, bw - 16, 3, K.ink); px(g, bx + 8, gy - 5, bw - 16, 1, NEON[col]); L(bx + 8, gy - 6, bw - 16, 3, NEON[col], 0.4, true);
    door(g, bx + 12, gy + 6, 24, gf - 8, NEON[col], L); frame(g, bx + 9, gy + 3, 30, gf - 3, NEON[col]);
    display(g, bx + 42, gy + 12, 14, 16, dk(NEON[col], 0.2), L, false);
    lantern(g, bx + bw - 12, gy + 6, i % 2 ? K.amber : K.red, L);
  }
  plant(g, x + bw - 6, gy + gf - 20);
  vending(g, x + w - 26, gy + gf - 40, dk(K.pink, 0.4), L);
});

/** 24h car park: open deck, a barrier gate and ticket machine */
registerFront('kabukiEastParking', { wall: K.concrete, upper: 'none' }, ({ g, x, y, w, h, gf, gy, L }) => {
  // open upper deck: dark opening with lit ceiling, pillars and car backs
  const oy = y + 14, oh = gy - oy - 8, ow = w - 76;
  px(g, x + 8, oy - 2, ow + 4, oh + 4, K.ink); px(g, x + 10, oy, ow, oh, K.glassLo);
  bands(g, x + 10, oy, ow, oh, dk(K.glassLo, 0.3), K.glassLo, 3);
  for (let i = 20; i < ow; i += 40) { px(g, x + 10 + i, oy + 1, 14, 2, K.warmHi); L(x + 10 + i, oy, 14, 3, K.amber, 0.3); }
  for (let i = 0; i <= ow; i += 40) px(g, x + 10 + i, oy, 5, oh, K.concreteLo);
  for (let i = 8; i < ow - 20; i += 40) { const c = dk([K.pink, K.cyan, K.amber][Math.floor(hash2(x + i, y) * 3)], 0.5); px(g, x + 10 + i, oy + oh - 9, 24, 8, K.ink); px(g, x + 11 + i, oy + oh - 8, 22, 6, c); px(g, x + 13 + i, oy + oh - 7, 18, 2, K.glassHi); }
  px(g, x + 8, oy + oh + 2, ow + 4, 3, K.concreteHi); // slab edge
  bandSign(g, x + w - 36, y + 12, 'パーキング', 'amber', L, 1);
  // ground floor: entrance ramp with a striped barrier arm, exit lane, ticket machine
  px(g, x + 10, gy + 4, 96, gf - 6, K.ink); bands(g, x + 12, gy + 6, 92, gf - 10, K.glassLo, K.ink, 4);
  dither(g, x + 12, gy + 6, 92, 12, alpha(K.amber, 0.3)); L(x + 14, gy + gf - 14, 88, 8, K.amber, 0.3, true);
  px(g, x + 20, gy + 24, 70, 4, K.ink);
  for (let i = 0; i < 70; i += 10) px(g, x + 20 + i, gy + 24, 5, 4, i % 20 ? K.white : K.red);
  px(g, x + 16, gy + 14, 6, gf - 16, K.metal); px(g, x + 17, gy + 14, 2, gf - 16, K.metalHi); px(g, x + 15, gy + 18, 8, 5, K.warmHi); L(x + 14, gy + 17, 10, 7, K.amber, 0.5, true);
  px(g, x + 112, gy + 6, 3, gf - 8, K.concreteLo); // pillar
  door(g, x + 124, gy + 6, 28, gf - 8, K.cyan, L);
  vending(g, x + 160, gy + gf - 40, dk(K.cyan, 0.45), L);
});

/** coin laundry: washers glowing behind a cool lit glass front */
registerFront('kabukiEastLaundry', { wall: K.plaster }, ({ g, x, y, w, gf, gy, L }) => {
  bladeSign(g, x + 8, y + 12, 'ランドリー', 'cyan', L, 2);
  px(g, x + 40, gy - 8, w - 56, 3, K.ink); px(g, x + 41, gy - 7, w - 58, 1, K.cyan); L(x + 40, gy - 8, w - 56, 3, K.cyan, 0.35, true);
  display(g, x + 40, gy + 4, w - 56, gf - 12, dk(K.cyan, 0.15), L, false);
  for (let i = 0; i < 4; i++) { // front-loading washers
    const wx = x + 50 + i * 36;
    px(g, wx, gy + 10, 30, 28, K.ink); px(g, wx + 1, gy + 11, 28, 26, K.white); px(g, wx + 1, gy + 11, 28, 3, K.plaster);
    disc(g, wx + 15, gy + 26, 9, 9, K.ink); disc(g, wx + 15, gy + 26, 7, 7, K.glass); disc(g, wx + 13, gy + 24, 3, 3, K.glassHi);
    px(g, wx + 4, gy + 14, 8, 2, K.cyan); px(g, wx + 22, gy + 14, 3, 2, i % 2 ? K.pink : K.amber);
  }
  px(g, x + 40, gy + gf - 8, w - 56, 3, K.concreteHi);
  plant(g, x + 14, gy + gf - 20);
});
